import { useParams, useNavigate } from 'react-router-dom';
import { useState, useCallback, useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import { Scrollbars } from 'react-custom-scrollbars-2';

import Card from 'components/card/Card';
import Paginate from 'components/paginate/Paginate';

import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Text,
  Flex,
  useColorModeValue,
  Grid,
  Box,
  HStack,
  Heading,
  Spinner,
  Button,
  VStack,
  Icon,
  Badge,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  List,
  ListItem,
  useDisclosure,
  useToast,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';

import { PhoneIcon, EmailIcon, InfoIcon, SearchIcon } from '@chakra-ui/icons';

import {
  apiGetCustomerOverview,
  apiSearch,
  apiAddCustomerRelationship,
} from 'api';

import getDateTimeString from 'helpers/getDateTimeString';
import getDateString from 'helpers/getDateString';

import type {
  APICustomerOverview,
  APIPaginatedResponse,
  APISearchData,
} from 'types/api.types';

const CustomerOverviewView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose: closeModal } = useDisclosure();
  const toast = useToast();

  // Pagination state for each section
  const [wipPage, setWipPage] = useState(0);
  const [policiesPage, setPoliciesPage] = useState(0);
  const [activitiesPage, setActivitiesPage] = useState(0);

  // Search state for Add Client modal
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [relationshipType, setRelationshipType] = useState('');

  // Handle modal close and reset state
  const onClose = () => {
    setSearchValue('');
    setSearchResults([]);
    setSelectedCustomer(null);
    setRelationshipType('');
    closeModal();
  };

  const { error, data, isLoading } = useQuery({
    queryKey: ['customer-overview', id, wipPage, policiesPage, activitiesPage],
    queryFn: () =>
      apiGetCustomerOverview(
        parseInt(id!),
        wipPage + 1,
        policiesPage + 1,
        activitiesPage + 1,
      ),
    enabled: !!id,
    staleTime: 0, // Always fetch fresh data
    gcTime: 0, // Don't cache the data
  }) as {
    data: APICustomerOverview;
    isLoading: boolean;
    error: any;
  };

  // Reset modal states and pagination when customer ID changes
  useEffect(() => {
    setSearchValue('');
    setSearchResults([]);
    setSelectedCustomer(null);
    setRelationshipType('');
    setWipPage(0);
    setPoliciesPage(0);
    setActivitiesPage(0);
  }, [id]);

  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');
  const orange500 = useColorModeValue('orange.500', 'orange.400');
  const orange100 = useColorModeValue('orange.100', 'orange.900');
  const hoverBgColor = useColorModeValue('gray.50', 'whiteAlpha.100');
  const hoverBgColorDarker = useColorModeValue('gray.100', 'whiteAlpha.200');
  const pageBg = useColorModeValue('background.100', 'background.900');

  // Helper function to get icon for activity source
  const getActivitySourceIcon = (source: string) => {
    const lowerSource = source.toLowerCase();
    if (lowerSource.includes('lead')) return EmailIcon;
    if (lowerSource.includes('billing') || lowerSource.includes('payment'))
      return PhoneIcon;
    return InfoIcon;
  };

  // Handler for recent WIP item click navigation
  const handleRecentWIPClick = (policyUuid: string) => {
    if (policyUuid) {
      navigate(`/admin/policies/overview/${policyUuid}`);
    }
  };

  // Handler for activity click navigation
  const handleActivityClick = (source: string, sourceId: number) => {
    const lowerSource = source.toLowerCase();
    if (lowerSource.includes('lead')) {
      navigate(`/admin/leads/${sourceId}`);
    } else if (lowerSource.includes('policy')) {
      navigate(`/admin/policies/overview/${sourceId}`);
    } else if (lowerSource.includes('submission')) {
      navigate(`/admin/submissions/${sourceId}`);
    }
    // Add more source types as needed
  };

  // Handler for viewing a related client
  const handleViewClient = (clientId: number) => {
    navigate(`/admin/customers/${clientId}`);
  };

  // Perform the actual search
  const performSearch = useCallback(async () => {
    const value = searchValue.trim();

    if (!value) {
      toast({
        title: 'Search Required',
        description: 'Please enter a policy number to search',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSearching(true);
    try {
      const response = await apiSearch(value);
      const data = response as APIPaginatedResponse<APISearchData>;

      // Format results using customer data from search API response
      const resultsWithCustomers = data.results.map((result) => ({
        id: result.uuid,
        policy_nr: result.policy_nr,
        title: `Policy #${result.policy_nr || result.uuid}`,
        footnote: `Created on ${new Date(
          result.created_at,
        ).toLocaleDateString()}, Broker: ${result.broker}`,
        customer_id: result.customer_id,
        customer_name: result.customer_name || result.broker,
        customer_email: result.customer_email || '',
        customer_phone: result.customer_phone || '',
      }));

      setSearchResults(resultsWithCustomers);

      if (resultsWithCustomers.length === 0) {
        toast({
          title: 'No Results',
          description: 'No policies found matching your search',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      setSearchResults([]);
      toast({
        title: 'Search Error',
        description: 'Failed to search for policies',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSearching(false);
    }
  }, [searchValue, toast]);

  // Handle Enter key press
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      performSearch();
    }
  };

  // Handler when a search result is selected in the modal
  const handleSearchResultClick = (result: any) => {
    setSelectedCustomer(result);
  };

  // Handler to add the relationship
  const handleAddRelationship = async () => {
    if (
      !selectedCustomer ||
      !relationshipType ||
      !selectedCustomer.customer_id
    ) {
      toast({
        title: 'Missing Information',
        description: 'Please select a customer and relationship type',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await apiAddCustomerRelationship(
        parseInt(id!),
        selectedCustomer.customer_id,
        relationshipType,
      );

      toast({
        title: 'Relationship Added',
        description: `Successfully linked ${selectedCustomer.customer_name} as ${relationshipType}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Reset modal state
      setSearchValue('');
      setSearchResults([]);
      setSelectedCustomer(null);
      setRelationshipType('');
      onClose();

      // Refresh the page to show the new relationship
      window.location.reload();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to add relationship',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (isLoading) {
    return (
      <Card textAlign="center">
        <Spinner size="xl" />

        <Text mt="4">Loading customer details...</Text>
      </Card>
    );
  }

  if (error) {
    return (
      <Card textAlign="center">
        <Alert status="error">
          <AlertIcon />

          <AlertTitle>Error</AlertTitle>

          <AlertDescription>{JSON.stringify(error)}</AlertDescription>
        </Alert>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card textAlign="center">
        <Text>Customer not found</Text>
      </Card>
    );
  }

  return (
    <Scrollbars
      autoHide
      style={{
        height: 'calc(100vh - 100px)',
      }}
    >
      <Box bg={pageBg} minH="100vh" p="6">
        <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap="6" h="100%">
          {/* Main Content Area */}

          <VStack spacing="4" align="stretch">
            {/* Policies Section */}

            <Box bg={cardBg} borderRadius="lg" shadow="sm" p="4">
              <Flex justify="space-between" align="center" mb="3">
                <Heading size="md" color={textColor} fontWeight="600">
                  Policies
                </Heading>
                <Text color={labelColor} fontSize="sm">
                  {data.policies_count || 0} policies
                </Text>
              </Flex>

              {data.policies && data.policies.length > 0 ? (
                <>
                  <Box overflowX="auto">
                    <Table variant="simple" size="sm">
                      {/* @ts-ignore */}
                      <Thead align="center">
                        {/* @ts-ignore */}
                        <Tr>
                          <Th>Policy Number</Th>

                          <Th>Broker</Th>

                          <Th>LOB</Th>

                          <Th>Status</Th>

                          <Th>Effective Date</Th>
                        </Tr>
                      </Thead>

                      <Tbody>
                        {data.policies.map((policy: any) => (
                          <Tr
                            key={policy.id}
                            cursor="pointer"
                            _hover={{ bg: hoverBgColor }}
                            onClick={() =>
                              navigate(
                                `/admin/policies/overview/${policy.policy_uuid}`,
                              )
                            }
                          >
                            <Td>
                              <Text
                                fontSize="sm"
                                fontWeight="600"
                                color={textColor}
                              >
                                {policy.policy_nr || '-'}
                              </Text>
                            </Td>

                            <Td>
                              <Text fontSize="sm" color={textColor}>
                                {policy.broker_name || '-'}
                              </Text>
                            </Td>

                            <Td>
                              <Text fontSize="sm" color={textColor}>
                                {policy.life_product && policy.line_of_business === 'life'
                                  ? `Life/${policy.life_product}`
                                  : policy.line_of_business_display ||
                                  policy.line_of_business ||
                                  '-'}
                              </Text>
                            </Td>

                            <Td>
                              <Badge colorScheme="green" fontSize="xs">
                                {policy.status_display || policy.status || '-'}
                              </Badge>
                            </Td>

                            <Td>
                              <Text fontSize="sm" color={textColor}>
                                {policy.effective_date
                                  ? new Date(
                                      policy.effective_date,
                                    ).toLocaleDateString()
                                  : '-'}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                  {data.policies_count > 0 && (
                    <Box mt="4">
                      <Paginate
                        page={policiesPage}
                        count={data.policies_count}
                        onSetPage={setPoliciesPage}
                        pageSize={10}
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Text color={labelColor} textAlign="center" py="4">
                  No policies found for this customer
                </Text>
              )}
            </Box>

            {/* Quotes Section */}

            <Box bg={cardBg} borderRadius="lg" shadow="sm" p="4">
              <Heading size="md" color={textColor} mb="3" fontWeight="600">
                Quotes
              </Heading>

              <Flex align="center" justify="center" py="4">
                <VStack spacing="1">
                  <Icon as={InfoIcon} w="8" h="8" color={labelColor} />
                  <Text color={labelColor} fontSize="md" fontWeight="500">
                    Coming Soon
                  </Text>
                </VStack>
              </Flex>
            </Box>

            {/* Work in Progress */}

            <Box bg={cardBg} borderRadius="lg" shadow="sm" p="4">
              <Flex justify="space-between" align="center" mb="3">
                <Heading size="md" color={textColor} fontWeight="600">
                  Work in Progress
                </Heading>
                <Text color={labelColor} fontSize="sm">
                  {data.wip_count || 0} items
                </Text>
              </Flex>
              {data.recent_wip_items && data.recent_wip_items.length > 0 ? (
                <>
                  <VStack spacing="3" align="stretch">
                    {data.recent_wip_items.map((wip) => (
                      <Box
                        key={wip.id}
                        p="4"
                        borderRadius="md"
                        bg={cardBg}
                        borderLeft="4px solid"
                        borderLeftColor="blue.500"
                        cursor="pointer"
                        onClick={() => handleRecentWIPClick(wip.policy_uuid)}
                        _hover={{ bg: hoverBgColorDarker }}
                        transition="background 0.2s"
                      >
                        <Flex justify="space-between" align="flex-start" mb="2">
                          <VStack align="flex-start" spacing="1" flex="1">
                            <HStack spacing="2">
                              <Text
                                color={textColor}
                                fontSize="sm"
                                fontWeight="600"
                              >
                                Policy: {wip.policy_nr || '- '}
                              </Text>
                              {wip.type_display && (
                                <Badge colorScheme="blue" fontSize="xs">
                                  {wip.type_display === 'Life' && wip.life_product
                                    ? `Life/${wip.life_product}`
                                    : wip.type_display}
                                </Badge>
                              )}
                            </HStack>
                            <HStack spacing="2" flexWrap="wrap">
                              <Badge colorScheme="purple" fontSize="xs">
                                {wip.wip_status_display}
                              </Badge>

                              <Badge colorScheme="green" fontSize="xs">
                                {wip.work_type_display}
                              </Badge>
                              {wip.carrier_name && (
                                <Text fontSize="xs" color={labelColor}>
                                  Carrier: {wip.carrier_name}
                                </Text>
                              )}
                            </HStack>
                          </VStack>
                          <VStack align="flex-end" spacing="1">
                            {wip.premium && (
                              <Text
                                fontSize="sm"
                                fontWeight="600"
                                color={textColor}
                              >
                                ${wip.premium.toLocaleString()}
                              </Text>
                            )}
                            {wip.effective_date && (
                              <Text fontSize="xs" color={labelColor}>
                                {new Date(
                                  wip.effective_date,
                                ).toLocaleDateString()}
                              </Text>
                            )}
                          </VStack>
                        </Flex>
                      </Box>
                    ))}
                  </VStack>
                  {data.wip_count > 0 && (
                    <Box mt="4">
                      <Paginate
                        page={wipPage}
                        count={data.wip_count}
                        onSetPage={setWipPage}
                        pageSize={10}
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Text color={labelColor} textAlign="center" py="4">
                  No work in progress items found for this customer
                </Text>
              )}
            </Box>

            {/* Activity Timeline */}

            <Box bg={cardBg} borderRadius="lg" shadow="sm" p="4">
              <Flex justify="space-between" align="center" mb="3">
                <Heading size="md" color={textColor} fontWeight="600">
                  Activity Timeline
                </Heading>
                <Text color={labelColor} fontSize="sm">
                  {data.activity_count || 0} activities
                </Text>
              </Flex>
              {data.recent_activities && data.recent_activities.length > 0 ? (
                <>
                  <VStack align="stretch" spacing="3">
                    {data.recent_activities.map((activity, idx) => {
                      const ActivityIcon = getActivitySourceIcon(
                        activity.source,
                      );
                      return (
                        <Flex
                          key={idx}
                          gap="3"
                          pb="3"
                          borderBottom="1px solid"
                          borderColor="gray.100"
                          _last={{ borderBottom: 'none' }}
                          cursor="pointer"
                          onClick={() =>
                            handleActivityClick(
                              activity.source,
                              activity.source_id,
                            )
                          }
                          _hover={{ bg: hoverBgColor }}
                          borderRadius="md"
                          p="2"
                          transition="background 0.2s"
                        >
                          <Flex flexShrink={0}>
                            <Box
                              w="10"
                              h="10"
                              bg={orange100}
                              borderRadius="full"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Icon
                                as={ActivityIcon}
                                w="5"
                                h="5"
                                color={orange500}
                              />
                            </Box>
                          </Flex>
                          <Box flex="1">
                            <Flex justify="space-between" align="center" mb="1">
                              <HStack spacing="2">
                                <Text
                                  color={textColor}
                                  fontSize="sm"
                                  fontWeight="500"
                                >
                                  {activity.user}
                                </Text>

                                <Badge colorScheme="blue" fontSize="xs">
                                  {activity.source_name}
                                </Badge>
                              </HStack>
                              <Text color={labelColor} fontSize="xs">
                                {getDateTimeString(
                                  new Date(activity.created_at),
                                )}
                              </Text>
                            </Flex>
                            <Text
                              fontSize="sm"
                              color={textColor}
                              dangerouslySetInnerHTML={{
                                __html: activity.text,
                              }}
                            />
                          </Box>
                        </Flex>
                      );
                    })}
                  </VStack>
                  {data.activity_count > 0 && (
                    <Box mt="4">
                      <Paginate
                        page={activitiesPage}
                        count={data.activity_count}
                        onSetPage={setActivitiesPage}
                        pageSize={10}
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Text color={labelColor} textAlign="center" py="4">
                  No activity found for this customer
                </Text>
              )}
            </Box>
          </VStack>

          {/* Right Sidebar - Client Card */}
          <Box>
            <Box
              bg={cardBg}
              borderRadius="lg"
              shadow="sm"
              overflow="hidden"
              position="sticky"
              top="6"
            >
              {/* Client Card Header */}

              <Box
                bgGradient="linear(to-r, orange.500, orange.600)"
                color="white"
                px="6"
                py="4"
              >
                <Flex justify="space-between" align="center" mb="2">
                  <Text fontSize="lg" fontWeight="600">
                    CLIENT CARD
                  </Text>

                  <Tooltip label="Coming Soon" placement="bottom">
                    <Button
                      size="sm"
                      variant="ghost"
                      color="white"
                      _hover={{ color: 'orange.100' }}
                    >
                      <Text fontSize="sm">Edit Client</Text>
                    </Button>
                  </Tooltip>
                </Flex>
              </Box>

              {/* Client Info */}
              <Box
                px="6"
                py="4"
                borderBottom="1px solid"
                borderColor="gray.200"
              >
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  color={textColor}
                  mb="1"
                  textTransform="uppercase"
                >
                  {data.first_name} {data.last_name}
                </Text>
                <Text fontSize="sm" color={labelColor} mb="3">
                  DOB:{' '}
                  {data.date_of_birth
                    ? getDateString(new Date(data.date_of_birth))
                    : 'Not provided'}
                </Text>

                <VStack spacing="2" align="stretch">
                  <Box>
                    <Text
                      fontSize="sm"
                      fontWeight="500"
                      color={labelColor}
                      mb="1"
                    >
                      Address:
                    </Text>
                    <Text fontSize="sm" color={textColor}>
                      {data.full_address || data.address || 'Not provided'}
                    </Text>
                  </Box>

                  <HStack>
                    <Icon as={PhoneIcon} w="4" h="4" color={labelColor} />
                    <Text fontSize="sm" color={textColor}>
                      {data.phone_number || 'Not provided'}
                    </Text>
                  </HStack>

                  <HStack>
                    <Icon as={EmailIcon} w="4" h="4" color={labelColor} />
                    <Text fontSize="sm" color={textColor}>
                      {data.email || 'Not provided'}
                    </Text>
                  </HStack>
                </VStack>
              </Box>

              {/* Action Buttons */}
              <Box
                px="6"
                py="4"
                borderBottom="1px solid"
                borderColor="gray.200"
              >
                <VStack spacing="3" align="stretch">
                  <Button
                    as="a"
                    href={`tel:${data.phone_number || ''}`}
                    w="full"
                    bg="white"
                    border="2px solid"
                    borderColor="blue.500"
                    color="blue.500"
                    _hover={{ bg: 'blue.50' }}
                    leftIcon={<PhoneIcon />}
                    isDisabled={!data.phone_number}
                  >
                    CALL CLIENT
                  </Button>

                  <Button
                    as="a"
                    href={`mailto:${data.email || ''}`}
                    w="full"
                    bg={orange500}
                    color="white"
                    _hover={{ bg: 'orange.600' }}
                    leftIcon={<EmailIcon />}
                    isDisabled={!data.email}
                  >
                    SEND CLIENT EMAIL
                  </Button>
                </VStack>
              </Box>

              {/* Related Clients */}
              <Box
                px="6"
                py="4"
                borderBottom="1px solid"
                borderColor="gray.200"
              >
                <Flex justify="space-between" align="center" mb="3">
                  <Text fontWeight="600" color={textColor}>
                    RELATED CLIENTS
                  </Text>
                  <Button
                    bg={orange500}
                    color="white"
                    size="xs"
                    _hover={{ bg: 'orange.600' }}
                    onClick={onOpen}
                  >
                    + Add Client
                  </Button>
                </Flex>

                {/* Related Clients List */}
                {data.related_clients && data.related_clients.length > 0 ? (
                  <VStack spacing="2" align="stretch">
                    {data.related_clients.map((client: any) => (
                      <Flex
                        key={client.id}
                        justify="space-between"
                        align="center"
                        py="2"
                        borderBottom="1px solid"
                        borderColor="gray.100"
                        _last={{ borderBottom: 'none' }}
                      >
                        <HStack>
                          <Icon as={InfoIcon} w="4" h="4" color={labelColor} />
                          <Box>
                            <Text
                              fontSize="sm"
                              fontWeight="500"
                              color={textColor}
                            >
                              {client.first_name} {client.last_name}
                            </Text>
                            {client.relationship_type && (
                              <Text fontSize="xs" color={labelColor}>
                                {client.relationship_type}
                              </Text>
                            )}
                          </Box>
                        </HStack>
                        <Text
                          fontSize="xs"
                          color="blue.600"
                          _hover={{ color: 'blue.800' }}
                          cursor="pointer"
                          onClick={() => handleViewClient(client.id)}
                        >
                          View Client
                        </Text>
                      </Flex>
                    ))}
                  </VStack>
                ) : (
                  <Text
                    color={labelColor}
                    fontSize="sm"
                    textAlign="center"
                    py="3"
                  >
                    No related clients yet
                  </Text>
                )}
              </Box>

              {/* Quick Links */}
              <Box px="6" py="4">
                <Text fontWeight="600" color={textColor} mb="3">
                  QUICK LINKS
                </Text>

                <VStack spacing="2" align="stretch">
                  <Flex align="center" justify="center" py="2">
                    <VStack spacing="1">
                      <Icon as={InfoIcon} w="8" h="8" color={labelColor} />
                      <Text color={labelColor} fontSize="md" fontWeight="500">
                        Coming Soon
                      </Text>
                    </VStack>
                  </Flex>
                </VStack>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Box>

      {/* Add Client Modal */}

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />

        <ModalContent>
          <ModalHeader>Add Related Client</ModalHeader>

          <ModalCloseButton />

          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              {/* Search Input */}
              <Box>
                <Text fontSize="sm" fontWeight="500" mb={2}>
                  Search by Policy Number
                </Text>

                <InputGroup>
                  <InputLeftElement>
                    <SearchIcon color={labelColor} />
                  </InputLeftElement>

                  <Input
                    placeholder="Enter policy number and press Enter..."
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />

                  <InputRightElement width="4.5rem">
                    <IconButton
                      aria-label="Search"
                      icon={<SearchIcon />}
                      size="sm"
                      colorScheme="blue"
                      onClick={performSearch}
                      isLoading={isSearching}
                    />
                  </InputRightElement>
                </InputGroup>
              </Box>

              {/* Search Results */}
              {isSearching && (
                <Flex justify="center" align="center" py={4}>
                  <Spinner size="sm" mr={2} />
                  <Text>Searching...</Text>
                </Flex>
              )}

              {!isSearching && searchResults.length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="500" mb={2}>
                    Search Results
                  </Text>

                  <List
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    maxH="200px"
                    overflowY="auto"
                  >
                    {searchResults.map((result) => (
                      <ListItem
                        key={result.id}
                        p={3}
                        cursor="pointer"
                        borderBottom="1px solid"
                        borderColor="gray.100"
                        _last={{ borderBottom: 'none' }}
                        _hover={{ bg: hoverBgColor }}
                        onClick={() => handleSearchResultClick(result)}
                        bg={
                          selectedCustomer?.id === result.id
                            ? 'blue.50'
                            : 'transparent'
                        }
                      >
                        <Text fontWeight="500">{result.title}</Text>
                        {result.footnote && (
                          <Text fontSize="sm" color={labelColor}>
                            {result.footnote}
                          </Text>
                        )}
                        {result.customer_name && (
                          <Text fontSize="sm" color="blue.600" mt={1}>
                            Customer: {result.customer_name}
                          </Text>
                        )}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {!isSearching && searchValue && searchResults.length === 0 && (
                <Text color={labelColor} textAlign="center" py={4}>
                  No results found
                </Text>
              )}

              {/* Selected Customer Display */}
              {selectedCustomer && (
                <Box
                  p={4}
                  bg="green.50"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="green.200"
                >
                  <Text fontSize="sm" fontWeight="600" color="green.700" mb={2}>
                    Selected Customer
                  </Text>
                  <Text fontSize="sm">{selectedCustomer.customer_name}</Text>
                  <Text fontSize="xs" color={labelColor}>
                    {selectedCustomer.title}
                  </Text>
                </Box>
              )}

              {/* Relationship Type Selection */}
              {selectedCustomer && (
                <Box>
                  <Text fontSize="sm" fontWeight="500" mb={2}>
                    Relationship Type
                  </Text>

                  <Select
                    placeholder="Select relationship type"
                    value={relationshipType}
                    onChange={(e) => setRelationshipType(e.target.value)}
                  >
                    <option value="spouse">Spouse</option>
                    <option value="parent">Parent</option>
                    <option value="child">Child</option>
                    <option value="sibling">Sibling</option>
                    <option value="other">Other</option>
                    <option value="business_partner">Business Partner</option>
                  </Select>
                </Box>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              bg={orange500}
              color="white"
              _hover={{ bg: 'orange.600' }}
              onClick={handleAddRelationship}
              isDisabled={!selectedCustomer || !relationshipType}
            >
              Add Relationship
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Scrollbars>
  );
};

export default CustomerOverviewView;
